/**
 * One editable central setting: label, description, current value (or what the
 * default means), and inline edit / save / reset. Shared by every Settings page.
 *
 * Endpoints:
 *   PUT    /api/admin/settings/:key   { value }
 *   DELETE /api/admin/settings/:key   (reset to default)
 */
import React, { useState } from 'react';
import { RotateCcw, Pencil, Check, X } from 'lucide-react';
import { adminMutate } from '../../../utils/adminApi';
import {
  AdminSetting, changedLine, coerceDraft, displayValue, errorMessage, initialDraft,
} from './settingsLogic';

interface Props {
  setting: AdminSetting;
  canEdit: boolean;
  onChanged: () => void;
}

export const SettingField: React.FC<Props> = ({ setting, canEdit, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(initialDraft(setting));
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    const { value, error: localError } = coerceDraft(setting, draft);
    if (localError) {
      setError(localError);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminMutate(`/api/admin/settings/${encodeURIComponent(setting.key)}`, 'PUT', { value });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminMutate(`/api/admin/settings/${encodeURIComponent(setting.key)}`, 'DELETE');
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const changed = changedLine(setting);

  return (
    <div className="flex flex-col gap-2 border-b border-gray-100 py-4 last:border-b-0 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 sm:max-w-md">
        <div className="text-sm font-semibold text-gray-900">{setting.label}</div>
        <div className="mt-0.5 text-xs text-gray-500">{setting.description}</div>
        {changed && <div className="mt-1 text-[11px] text-gray-400">{changed}</div>}
      </div>

      <div className="flex min-w-0 flex-col items-stretch gap-2 sm:items-end">
        {!editing ? (
          <div className="flex items-center gap-2">
            <span
              className={`truncate text-sm ${setting.is_overridden ? 'font-medium text-gray-900' : 'text-gray-500'}`}
              title={displayValue(setting)}
            >
              {setting.is_overridden ? displayValue(setting) : `Default: ${displayValue(setting)}`}
            </span>
            {canEdit && (
              <button
                type="button"
                onClick={startEdit}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
            )}
            {canEdit && setting.is_overridden && (
              <button
                type="button"
                onClick={reset}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title={`Reset to default (${setting.default_display})`}
              >
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {setting.type === 'enum' ? (
              <select
                value={draft}
                onChange={e => setDraft(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              >
                {setting.choices.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            ) : (
              <input
                type={setting.type === 'int' ? 'number' : 'text'}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                min={setting.min_value ?? undefined}
                max={setting.max_value ?? undefined}
                className="w-64 rounded-md border border-gray-300 px-2 py-1 text-sm"
                autoFocus
              />
            )}
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: '#1F6C4F' }}
            >
              <Check className="h-3 w-3" /> Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
          </div>
        )}
        {error && <div className="text-xs text-red-600" role="alert">{error}</div>}
      </div>
    </div>
  );
};
